import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CopyIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, ClickAwayListener, Menu, MenuItem } from "@mui/material";
import { type FC, type MouseEvent, useCallback, useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthProvider";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useCopyToClipboard } from "../../hooks/useCopyToClipboard";
import type { SpecificPreference } from "../../utils/models";
import { portalOverlayProps } from "../UIToolkit/Popups/portalOverlay";

const PTOEditMenuContent: FC = () => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			gap: 1,
		}}
	>
		<EditIcon />
		Edit PTO
	</Box>
);

const PTODeleteMenuContent: FC = () => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			gap: 1,
		}}
	>
		<DeleteIcon />
		Delete PTO
	</Box>
);

const CopyMenuTriggerContent: FC = () => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			gap: 1,
			width: "100%",
		}}
	>
		<CopyIcon />
		Copy
		<ChevronRightIcon fontSize="small" sx={{ marginLeft: "auto" }} />
	</Box>
);

type PTOMenuProps = {
	preference: SpecificPreference;
	nurseId: number;
	actionsMenuAnchorEl: HTMLElement | null;
	onMenuClose: () => void;
	onEditPTO?: (preference: SpecificPreference, nurseId: number) => void;
	onDeletePTO?: (preference: SpecificPreference, nurseId: number) => void;
};

export const PTOMenu: FC<PTOMenuProps> = ({ preference, nurseId, actionsMenuAnchorEl, onMenuClose, onEditPTO, onDeletePTO }) => {
	const { track } = useAnalytics();
	const { showErrorSnackbar } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { userInfo } = useAuth();
	const { copyToClipboard } = useCopyToClipboard();
	const [copyMenuAnchorEl, setCopyMenuAnchorEl] = useState<HTMLElement | null>(null);

	useEffect(() => {
		if (actionsMenuAnchorEl) {
			track("pto actions menu opened", { preference_id: preference.id, nurse_id: nurseId });
		}
	}, [actionsMenuAnchorEl, preference.id, nurseId, track]);

	const handleActionsMenuClose = (): void => {
		if (copyMenuAnchorEl) return;
		onMenuClose?.();
	};

	const handleCopyMenuOpen = (event: MouseEvent<HTMLElement>): void => {
		event.stopPropagation();
		setCopyMenuAnchorEl(event.currentTarget);
	};

	const handleCopyMenuClose = (): void => {
		setCopyMenuAnchorEl(null);
	};

	const handleEditPTO = (): void => {
		track("Staff Table - edit PTO clicked", {
			source: "staff_table",
			preference_id: preference.id,
			nurse_id: nurseId,
		});
		onEditPTO?.(preference, nurseId);
		handleActionsMenuClose();
	};

	const handleDeletePTO = (): void => {
		track("Staff Table - delete PTO clicked", { source: "staff_table", preference_id: preference.id, nurse_id: nurseId });
		onDeletePTO?.(preference, nurseId);
		handleActionsMenuClose();
	};

	const copyPreferenceId = useCallback(async () => {
		const success = await copyToClipboard(preference.id.toString());
		if (success) showSnackbar("Preference ID copied to clipboard", "success");
		else showErrorSnackbar("Failed to copy to clipboard", {});
		setCopyMenuAnchorEl(null);
		onMenuClose();
	}, [preference.id, copyToClipboard, showSnackbar, showErrorSnackbar, onMenuClose]);

	const copyNurseId = useCallback(async () => {
		const success = await copyToClipboard(nurseId.toString());
		if (success) showSnackbar("Nurse ID copied to clipboard", "success");
		else showErrorSnackbar("Failed to copy to clipboard", {});
		setCopyMenuAnchorEl(null);
		onMenuClose();
	}, [nurseId, copyToClipboard, showSnackbar, showErrorSnackbar, onMenuClose]);

	return (
		<ClickAwayListener onClickAway={() => {}}>
			<div>
				<Menu
					anchorEl={actionsMenuAnchorEl}
					open={Boolean(actionsMenuAnchorEl)}
					onClose={handleActionsMenuClose}
					anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
					transformOrigin={{ vertical: "top", horizontal: "right" }}
					slotProps={{ paper: { sx: { mt: 1 } } }}
					{...portalOverlayProps("pto-actions-menu")}
				>
					<MenuItem onClick={handleEditPTO}>
						<PTOEditMenuContent />
					</MenuItem>
					<MenuItem onClick={handleDeletePTO}>
						<PTODeleteMenuContent />
					</MenuItem>
					{userInfo.support && (
						<MenuItem onClick={handleCopyMenuOpen}>
							<CopyMenuTriggerContent />
						</MenuItem>
					)}
				</Menu>
				<Menu
					anchorEl={copyMenuAnchorEl}
					open={Boolean(copyMenuAnchorEl)}
					onClose={handleCopyMenuClose}
					anchorOrigin={{ vertical: "top", horizontal: "right" }}
					transformOrigin={{ vertical: "top", horizontal: "left" }}
					{...portalOverlayProps("pto-copy-menu")}
				>
					<MenuItem onClick={copyPreferenceId}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<CopyIcon />
							Copy Preference ID
						</Box>
					</MenuItem>
					<MenuItem onClick={copyNurseId}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<CopyIcon />
							Copy Nurse ID
						</Box>
					</MenuItem>
				</Menu>
			</div>
		</ClickAwayListener>
	);
};
